import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import Layout from './Layout';
import MetaTags from './meta-tags';
import { useLanguage } from '../contexts/language-context';
import { 
  sortCollections,
  translateCollectionName,
  getCollectionTypeLabel
} from '../constants';
import { getLithuanianForm } from '../utils/lithuanian-grammar';
import apiService from '../services/api-service';
import { deduplicateById } from '../utils/deduplication';
import { SPINNER_CLASSES } from '../styles';
import scrollRestoration from '../utils/scroll-restoration';
import { useScrollRestoration } from '../hooks/use-scroll-restoration';

// Translations for sort options
const SORT_OPTIONS_TRANSLATIONS = {
  lt: [
    { value: 'likes', label: 'Populiariausi' },
    { value: 'items', label: 'Daugiausia elementų' },
    { value: 'name', label: 'Pagal pavadinimą' }
  ],
  en: [
    { value: 'likes', label: 'Most Popular' },
    { value: 'items', label: 'Most Items' },
    { value: 'name', label: 'By Name' }
  ],
  uk: [
    { value: 'likes', label: 'Найпопулярніші' },
    { value: 'items', label: 'Найбільше елементів' },
    { value: 'name', label: 'За назвою' }
  ]
};

const TYPE_FILTERS_TRANSLATIONS = {
  lt: [
    { value: 'all', label: 'Visi' },
    { value: 'movie', label: 'Filmai' },
    { value: 'show', label: 'TV Serialai' }
  ],
  en: [
    { value: 'all', label: 'All' },
    { value: 'movie', label: 'Movies' },
    { value: 'show', label: 'TV Shows' }
  ],
  uk: [
    { value: 'all', label: 'Всі' },
    { value: 'movie', label: 'Фільми' },
    { value: 'show', label: 'Серіали' }
  ]
};

function CollectionCard({ collection, locationKey }) {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const handleClick = () => {
    if (locationKey) {
      scrollRestoration.saveScrollPosition(locationKey, window.scrollY);
      scrollRestoration.setNavigatingToDetail(locationKey);
    }
    navigate(`/collections/${collection.id}`);
  };

  const typeColor = collection.type === 'movie' 
    ? 'bg-primary-600 text-white' 
    : collection.type === 'show'
    ? 'bg-purple-600 text-white'
    : 'bg-gray-600 text-white';

  const typeLabel = getCollectionTypeLabel(collection.type, language);
  const collectionName = translateCollectionName(collection.name, language);

  return (
    <button
      onClick={handleClick}
      className="group relative bg-dark-800/80 backdrop-blur-sm border border-dark-700 rounded-xl p-5 hover:border-primary-500/50 hover:bg-dark-700/80 transition-all duration-300 text-left w-full"
    >
      {/* Type Badge */}
      <div className="absolute top-4 right-4">
        <span className={`${typeColor} text-xs font-medium px-2.5 py-1 rounded-full`}>
          {typeLabel}
        </span>
      </div>

      {/* Collection Name */}
      <h3 className="text-lg font-semibold text-white group-hover:text-primary-300 transition-colors pr-20 mb-3 line-clamp-2">
        {collectionName}
      </h3>

      {/* User */}
      <p className="text-sm text-gray-400 mb-4">
        {t('curator')}: <span className="text-gray-300">{collection.user}</span>
      </p>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-1.5 text-gray-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span className="text-sm font-medium">{(collection.itemCount || 0).toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-400">
          <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <span className="text-sm font-medium">{collection.likes || 0}</span>
        </div>
      </div>

      {/* Hover Arrow */}
      <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </div>
    </button>
  );
}

function CollectionsPage() {
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('likes');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { t, language } = useLanguage();
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  
  const SORT_OPTIONS = SORT_OPTIONS_TRANSLATIONS[language] || SORT_OPTIONS_TRANSLATIONS.lt;
  const TYPE_FILTERS = TYPE_FILTERS_TRANSLATIONS[language] || TYPE_FILTERS_TRANSLATIONS.lt;

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch collections with infinite scroll
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useInfiniteQuery({
    queryKey: ['collections', debouncedSearch, typeFilter],
    queryFn: async ({ pageParam = 0 }) => {
      const params = { 
        limit: 50,
        offset: pageParam,
        type: typeFilter
      };
      
      if (debouncedSearch.trim().length >= 4) {
        params.search = debouncedSearch.trim();
      }
      
      return apiService.getCollections(params);
    },
    getNextPageParam: (lastPage, allPages) => {
      const hasMorePages = lastPage?.hasMore && lastPage.collections?.length === 50;
      return hasMorePages ? allPages.length * 50 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });

  // Flatten and deduplicate collections from all pages
  const allCollections = useMemo(() => {
    if (!data?.pages) return [];
    const flattened = data.pages.flatMap(page => page.collections || []);
    return deduplicateById(flattened);
  }, [data?.pages]);

  // Apply client-side sorting
  const filteredCollections = useMemo(() => {
    return sortCollections(allCollections, sortBy);
  }, [allCollections, sortBy]);

  // Scroll restoration for navigating back from collection detail pages
  const locationKey = useScrollRestoration({
    mediaType: 'collections',
    searchQuery: debouncedSearch,
    filters: { typeFilter, sortBy },
    isLoading,
    itemsLength: filteredCollections.length
  });

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const {
    data: heroStats,
    isPending: isHeroStatsPending,
    isError: isHeroStatsError,
  } = useQuery({
    queryKey: ['collections', 'stats'],
    queryFn: () => apiService.getCollectionsStats(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });

  return (
    <Layout>
      <MetaTags
        title="Kolekcijos - FilmųDB"
        description="Naršykite kuratorių sukurtas filmų ir TV serialų kolekcijas. Raskite geriausius filmus ir serialus pagal žanrą, platformą ar temą."
        url="https://filmudb.lt/collections"
        keywords="filmų kolekcijos, TV serialų sąrašai, geriausių filmų sąrašai, Netflix filmai, HBO serialai, kuratorių pasirinkimai"
      />

      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        {/* Hero Section */}
        <section className="relative py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">
              <span className="text-gradient">{t('curatorCollections')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              {t('collectionsDesc')}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8 text-center">
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
                <div className="text-xl sm:text-2xl font-bold text-primary-400">
                  {isHeroStatsPending ? '...' : isHeroStatsError ? '—' : heroStats?.total ?? '—'}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">{t('collectionsCount')}</div>
              </div>
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
                <div className="text-xl sm:text-2xl font-bold text-primary-400">
                  {isHeroStatsPending ? '...' : isHeroStatsError ? '—' : heroStats?.movieCount ?? '—'}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">{t('movieCollections')}</div>
              </div>
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
                <div className="text-xl sm:text-2xl font-bold text-primary-400">
                  {isHeroStatsPending ? '...' : isHeroStatsError ? '—' : heroStats?.showCount ?? '—'}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">{t('tvCollections')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="sticky top-[73px] z-40 backdrop-blur-xl border-b border-dark-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isLoading && searchQuery ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-500"></div>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </div>
              <input
                type="text"
                placeholder={t('searchCollections')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-primary w-full pl-10 py-2.5"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              {TYPE_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setTypeFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    typeFilter === filter.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-700 text-gray-300 hover:bg-dark-600 hover:text-white'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-primary py-2.5 px-4 min-w-[180px]"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading && !filteredCollections.length ? (
          <div className="flex justify-center items-center py-16">
            <div className={SPINNER_CLASSES.LARGE_PRIMARY}></div>
          </div>
        ) : isError ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-400 mb-2">Nepavyko įkelti kolekcijų</h3>
            <p className="text-gray-500">{error?.message || 'Pabandykite vėliau'}</p>
          </div>
        ) : filteredCollections.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-400 mb-2">Kolekcijų nerasta</h3>
            <p className="text-gray-500">
              {debouncedSearch.trim() && debouncedSearch.trim().length < 4 
                ? 'Įveskite bent 4 simbolius paieškai' 
                : 'Pabandykite pakeisti paieškos kriterijus'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-gray-400 text-sm">
              Rodoma {filteredCollections.length} {getLithuanianForm(filteredCollections.length, 'kolekcija', 'kolekcijos', 'kolekcijų')}
              {hasNextPage && ' (kraunama daugiau...)'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredCollections.map((collection, index) => (
                <CollectionCard key={`collection-${collection.id}-${index}`} collection={collection} locationKey={locationKey} />
              ))}
            </div>

            {/* Load More Trigger */}
            {hasNextPage && (
              <div
                ref={loadMoreRef}
                className="flex justify-center items-center py-8"
              >
                {isFetchingNextPage ? (
                  <div className={SPINNER_CLASSES.MEDIUM_PRIMARY}></div>
                ) : (
                  <span className="text-gray-500 text-sm">Kraunama daugiau...</span>
                )}
              </div>
            )}
          </>
        )}
      </main>
      </div>
    </Layout>
  );
}

export { CollectionsPage };
export default CollectionsPage;
